import React, { useEffect } from 'react'

import { Flex, Spinner, Text } from '@chakra-ui/react'
import { fetchAuthSession } from 'aws-amplify/auth'
// OAuthコールバック処理に必要。tree-shakingで除去されないよう明示的にimportする。
// ref: https://github.com/aws-amplify/amplify-js/issues/13688
import 'aws-amplify/auth/enable-oauth-listener'
import { Hub } from 'aws-amplify/utils'
import { useNavigate, useSearchParams } from 'react-router'
import { loginPathWithSSOParam } from 'src/features/auth/utils/loginPathWithSSOParam'
import { login } from 'src/legacy_modules/auth/login/request'

import { usePostLoginNavigation } from '../hooks/usePostLoginNavigation'

const SSO_ERROR_MESSAGES: Record<string, string> = {
  TENANT_NOT_ALLOWED:
    '許可されていない組織からのログインです。管理者にお問い合わせください。',
  USER_NOT_INVITED:
    'このアカウントは招待されていません。管理者にお問い合わせください。',
  LOCAL_ACCOUNT_ALREADY_ACTIVE:
    'このアカウントは別のログイン方法で登録されています。管理者にお問い合わせください。',
  INTERNAL_ERROR:
    'システムエラーが発生しました。しばらく時間を置いて再度お試しください。',
}

const DEFAULT_SSO_ERROR = 'SSOログインに失敗しました。再度お試しください。'

export const parseSsoErrorMessage = (
  errorDescription: string | null,
): string => {
  if (!errorDescription) return DEFAULT_SSO_ERROR

  for (const code of Object.keys(SSO_ERROR_MESSAGES)) {
    if (errorDescription.includes(code)) {
      return SSO_ERROR_MESSAGES[code]
    }
  }

  return DEFAULT_SSO_ERROR
}

export const OAuthCallback: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { jumpAfterLogin } = usePostLoginNavigation()

  useEffect(() => {
    // Cognitoがエラーをクエリパラメータで返す場合（例: PreSignUp Lambdaエラー）、
    // Hub.listenより先にイベントが発火してリスナーが間に合わないため、
    // クエリパラメータを直接チェックして即座に処理する
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    if (error) {
      navigate(loginPathWithSSOParam(), {
        replace: true,
        state: { ssoError: parseSsoErrorMessage(errorDescription) },
      })
      return
    }

    const handleSignIn = async () => {
      try {
        const currentSession = await fetchAuthSession()
        const idToken = currentSession.tokens?.idToken?.toString()
        if (!idToken) {
          navigate(loginPathWithSSOParam(), { replace: true })
          return
        }

        await login(idToken, false)
        await jumpAfterLogin()
      } catch {
        navigate(loginPathWithSSOParam(), { replace: true })
      }
    }

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        await handleSignIn()
      }
      if (payload.event === 'signInWithRedirect_failure') {
        navigate(loginPathWithSSOParam(), { replace: true })
      }
    })

    return unsubscribe
  }, [navigate, jumpAfterLogin, searchParams])

  return (
    <Flex w="100%" h="100vh" align="center" justify="center" direction="column">
      <Spinner size="lg" />
      <Text mt={4} color="gray.600">
        ログイン処理中...
      </Text>
    </Flex>
  )
}
